import { useState } from 'react';
import * as React from 'react';
import styled from '@emotion/styled';
import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import { tokens } from '@hotjar/hotsauce';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const checkboxInner = require('@hotjar/shared-assets/images/checkbox-inner.png');

// eslint-disable-next-line @typescript-eslint/no-var-requires
const cursorBlock = require('@hotjar/shared-assets/icons/cursor-block.ico');

let lastId = 0;
const generateId = (prefix = 'id') => {
  lastId += 1;
  return `${prefix}${lastId}`;
};

const Input = styled.input`
  opacity: 0;
  position: absolute;
`;

const labelStyles = css`
  display: inline-block;
  font-family: ${tokens.FONT_FAMILY}, sans-serif, Arial;
  line-height: 1.42857143;
  color: ${tokens.color.text_default};
  cursor: pointer;
  vertical-align: middle;
  font-size: 14px;
  font-weight: 400;
  text-transform: none;
  padding: 0 0 0 32px;
  text-align: left;
  margin: 0px;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 22px;
    height: 22px;
    background: ${tokens.color.ui_background};
    box-shadow: inset 0 0 0 1px ${tokens.color.ui_border};
    transition: all 0.2s linear;
    display: inline-block;
    vertical-align: middle;
    border-radius: 2px;
  }

  ${Input}:checked + &:before {
    box-shadow: inset 0 0 0 1px ${tokens.color.ui_active},
      inset 0 0 0 12px ${tokens.color.ui_active};
  }

  &:after {
    width: 16px;
    height: 14px;
    top: 5px;
    left: 3px;
    background: url(${checkboxInner}) 50% no-repeat;
    background-size: 16px;
    content: '';
    position: absolute;
    opacity: 0;
    transition: all 0.3s linear;
  }

  ${Input}:checked + &:after {
    opacity: 1;
  }

  ${Input}:disabled+& {
    opacity: 0.6;
    cursor: url(${cursorBlock}), default !important;
  }
  ${Input}:disabled+&:before {
    box-shadow: inset 0 0 0 1px ${tokens.color.ui_disabled},
      inset 0 0 0 12px ${tokens.color.ui_disabled} !important;
  }
  a {
    color: ${tokens.color.link};
  }
`;

const CheckboxContainer = styled.div`
  position: relative;
  display: block;
  min-height: 22px;
`;

const checkboxContainerSmallStyle = css`
  min-height: 20px;
`;

const labelSmallStyle = css`
  font-size: 12px;
  padding: 0 0 0 23px;

  &:before {
    top: 3px;
    width: 16px;
    height: 16px;
  }

  &:after {
    width: 14px;
    height: 12px;
    top: 6px;
    left: 1px;
    background-size: 13px;
  }
`;
const labelPartialStyle = css`
  &:after {
    width: 14px;
    height: 4px;
    top: 9px;
    left: 4px;
    background: ${tokens.color.ui_background};
    border-radius: 3px;
    transition-duration: 0s;
  }
`;
const labelSmallPartialStyle = css`
  &:after {
    width: 10px;
    left: 3px;
  }
`;

const Description = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.1px;
  color: ${tokens.color.text_default};
`;

interface CheckboxProps {
  disabled?: boolean;
  checked: boolean;
  partial: boolean;
  size?: 's' | 'm';
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  labelCss?: SerializedStyles;
  description?: string | React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  inputId?: string;
}

export const Checkbox = ({
  disabled,
  checked,
  partial,
  size = 'm',
  onChange,
  labelCss,
  description,
  children,
  className,
  inputId,
}: CheckboxProps): JSX.Element => {
  const [id] = useState(() => inputId || generateId('checkbox-'));
  const isSmall = size === 's';

  return (
    <CheckboxContainer css={[isSmall && checkboxContainerSmallStyle]} className={className}>
      <Input disabled={disabled} checked={checked} type="checkbox" id={id} onChange={onChange} />
      {children && (
        <label
          css={[
            labelStyles,
            isSmall && labelSmallStyle,
            partial && labelPartialStyle,
            isSmall && partial && labelSmallPartialStyle,
            labelCss,
          ]}
          htmlFor={id}
        >
          {children}
          {description && <Description>{description}</Description>}
        </label>
      )}
    </CheckboxContainer>
  );
};
